<template>
  <div class="position-fixed z-3 bottom-0 end-0 d-flex flex-column align-items-end w-100">
    <div v-if="quickReplies.length" class="quick-replies w-100 d-flex flex-wrap justify-content-end gap-2 px-3 mb-2">
      <button
        v-for="reply in quickReplies"
        :key="reply.value"
        type="button"
        class="quick-reply-bubble"
        @click="answerGuided(reply)"
      >
        {{ reply.label }}
      </button>
    </div>

    <div class="composer">
      <div class="input-sizer stacked flex-fill">
        <textarea :disabled="!composerEnabled" @keydown.enter="sendMessage" id="newMessage" class="form-control" rows="1" :placeholder="$t('message_composer.placeholder')" v-model="newMessage">
        </textarea>
      </div>
      <button :disabled="!composerEnabled" @click="sendMessage" type="button" class="btn messagerie-btn px-3 mb-2">
        <LetterSvg/>

        <span class="visually-hidden">{{ $t('message_composer.submit') }}</span>
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { sendAcCommand } from '@lib/utilities'
import LetterSvg from '@images/LetterSvg.vue'

const props = defineProps({
  subscription: Object,
  userActivities: Array,
  missionInfos: Object,
})

const newMessage = ref('')
// Guided questions answered on this client, hidden immediately on tap while the
// next question (or the persisted data.answer) has not arrived yet.
const answeredQuestionIds = ref([])

const sendBeneficiaryMessage = (message) => {
  sendAcCommand(props.subscription, 'send_message', { message })
}

const sendMessage = (event) => {
  event.preventDefault()
  // Typing a free-text reply instead of tapping Oui/Non is a valid way to
  // answer a guided question (it falls back to the LLM analysis) — dismiss
  // its quick-reply bubbles too, or they'd linger forever since nothing else
  // marks the question as answered on this client.
  if (pendingGuidedQuestion.value) {
    answeredQuestionIds.value.push(pendingGuidedQuestion.value.id)
  }
  sendBeneficiaryMessage(newMessage.value)
  newMessage.value = ''
}

// The latest guided depollution question still waiting for a Oui/Non answer.
const pendingGuidedQuestion = computed(() => {
  const questions = (props.userActivities || []).filter((activity) => {
    return activity.category === 'guided_depollution' &&
      activity.data &&
      activity.data.quick_replies &&
      activity.data.quick_replies.length > 0 &&
      !activity.data.answer &&
      !answeredQuestionIds.value.includes(activity.id)
  })
  return questions.length ? questions[questions.length - 1] : null
})

const quickReplies = computed(() => {
  return pendingGuidedQuestion.value ? pendingGuidedQuestion.value.data.quick_replies : []
})

const answerGuided = (reply) => {
  const question = pendingGuidedQuestion.value
  if (!question) return

  answeredQuestionIds.value.push(question.id)
  // The backend echoes the Oui/Non into the chat itself (AnswerService), so
  // sending it here too would duplicate it and trigger a needless LLM analysis
  // of the raw "Oui"/"Non" text.
  sendAcCommand(props.subscription, 'answer_guided_question', { activity_id: question.id, answer: reply.value })
}

const composerEnabled = computed(() => {
  const interventionAddressGeocoded = props.missionInfos?.mission_data?.intervention_address?.is_geocoded
  const sentGeolocation = props.userActivities?.some((activity) => {
    return activity.category === 'beneficiary_geolocation' && activity.data.state === 'confirmed'
  })
  const truckOnSite = props.userActivities?.some((activity) => {
    return activity.category === 'truck_on_site'
  })
  // Keep the composer open during the guided depollution flow so a free-text
  // answer can fall back to the LLM analysis at any step.
  const guidedFlowActive = props.userActivities?.some((activity) => {
    return activity.category === 'guided_depollution'
  })
  return interventionAddressGeocoded || sentGeolocation || truckOnSite || guidedFlowActive
})
</script>

<style scoped>
.quick-reply-bubble {
  border: 1px solid #0d6efd;
  color: #0d6efd;
  background: #fff;
  border-radius: 999px;
  padding: 6px 18px;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  transition: background-color 0.15s ease, color 0.15s ease;
}

.quick-reply-bubble:hover,
.quick-reply-bubble:focus {
  background: #0d6efd;
  color: #fff;
}
</style>
